import { CdkVirtualScrollableElement } from "@angular/cdk/scrolling";
import { Injectable, NgZone } from "@angular/core";
import { filterAllTruthyInTuple, pairBehaviorSubjects, subjectFromObs } from "core/rx-js";
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, interval, map, switchMap, take, takeUntil } from "rxjs";
import { IPageInfo, PdfViewerService } from "../../pdf-viewer.service";
import { IPdfDocument } from "../../types";


export type ExtendedPDFDocument = IPdfDocument & { viewportHeightOffset: number, viewportHeight: number }

export interface IExtendedPageInfo extends IPageInfo {

  pageLeft: number,
  pageRight: number
  prevPage?: IExtendedPageInfo
  nextPage?: IExtendedPageInfo
}

@Injectable()
export class PdfDocumentViewportsContainerService {

  private readonly _destroy = new Subject<void>();
  //todo
  readonly TITLE_OFFSET = 80;

  readonly virtualScrollableElementSubject = new BehaviorSubject<CdkVirtualScrollableElement>(undefined);
  get virtualScrollableElement() {
    return this.virtualScrollableElementSubject.value;
  }
  set virtualScrollableElement(value) {
    this.virtualScrollableElementSubject.next(value);
  }

  readonly scrollTopObs = this.virtualScrollableElementSubject.pipe(
    filter(Boolean),
    switchMap((virtualScrollableElement) => virtualScrollableElement.elementScrolled()),
    map((scrollEvent: Event) => {
      const element = scrollEvent.target as HTMLDivElement;
      return element.scrollTop;
    })

  )
  readonly scrollTopSubject = subjectFromObs({
    observable: this.scrollTopObs,
    initialValue: 0,
    until: this._destroy
  })

  readonly scrollableHeightSubject = subjectFromObs({
    observable: this._pdfViewerService.scrollerSubject.pipe(
      filter(Boolean),
      take(1),
      switchMap((scrollable) => interval(1000).pipe(map(() => scrollable))),
      map((scrollable) => scrollable.clientHeight),
      distinctUntilChanged()
    ),
    initialValue: 0,
    until: this._destroy
  })
  get scrollableHeight() {
    return this.scrollableHeightSubject.value
  }

  readonly scrollableWidthSubject = subjectFromObs({
    observable: this._pdfViewerService.scrollerSubject.pipe(
      filter(Boolean),
      take(1),
      switchMap((scrollable) => interval(1000).pipe(map(() => scrollable))),
      map((scrollable) => scrollable.clientWidth),
      distinctUntilChanged()
    ),
    initialValue: 0,
    until: this._destroy
  })

  get scrollableWidth() {
    return this.scrollableWidthSubject.value;
  }

  readonly pageOverlayStyleSubject = new BehaviorSubject<any>(null)
  readonly pdfDocumentViewportHeights = new BehaviorSubject<{ [docId: string]: number }>({})

  readonly viewportHeightSubject = new BehaviorSubject<string>('calc(100vh - 4rem)')
  readonly viewportResetSubject = new BehaviorSubject<boolean>(false)

  getDocumentViewportHeight(documentId: string) {
    return this.pdfDocumentViewportHeights.value[documentId];
  }

  /**pdfViewerService */
  readonly selectedPageSubject = this._pdfViewerService.selectedPageSubject;
  readonly selectedZoomSubject = this._pdfViewerService.selectedZoomSubject;
  readonly selectedPageIdxObs = this._pdfViewerService.selectedPageIdxObs;

  /**depSubjects */


  readonly totalHeightSubject = subjectFromObs({
    observable: this._pdfViewerService.scrollerSubject
      .pipe(
        filter(Boolean),
        switchMap(scroller => interval(1000)
          .pipe(map(() => scroller.scrollHeight))
        )
      ),
    initialValue: null,
    until: this._destroy
  })



  constructor(
    private readonly _pdfViewerService: PdfViewerService,
    private readonly _ngZone: NgZone
  ) {

    pairBehaviorSubjects(this.scrollableWidthSubject, this._pdfViewerService.scrollableWidthSubject);

    combineLatest([
      this.selectedPageIdxObs,
      this.pdfPageInfosSubject,
    ])
      .pipe(filterAllTruthyInTuple(), takeUntil(this._destroy))
      .subscribe(() => {
        this._scrollToSelectedPage()
      })


  }

  getScrollPageInfo(scrollTop: number) {

    let pageInfo = this._getPageIdxByScroll(scrollTop);

    if (!pageInfo) {
      pageInfo = this.pdfPageInfos?.find(() => true)
    }


    return this.getExtendedPage(pageInfo);
  }


  readonly pdfPageInfosSubject = this._pdfViewerService.pdfPageInfosSubject;
  get pdfPageInfos() {
    return this.pdfPageInfosSubject.value;
  }

  getScrollTopFromDocAndPage(pageIdx: number, documentId: string) {

    const page = this.pdfPageInfos.find((({ realPageIdx, docId }) => pageIdx === realPageIdx && documentId === docId))

    return Math.round(page.scrollTop);
  }

  getPageWidth(pageIdx: number, documentId: string) {

    const page = this.pdfPageInfos.find((({ realPageIdx, docId }) => pageIdx === realPageIdx && documentId === docId))

    return page.dimensions.width;
  }

  getPageWidthObs(pageIdx: number, documentId: string) {

    return this.pdfPageInfosSubject.pipe(
      filter(Boolean),
      take(1),
      map((pdfPageInfos) => pdfPageInfos.find((({ realPageIdx, docId }) => pageIdx === realPageIdx && documentId === docId))),
      map(page => page.dimensions.width)
    )
  }


  getPageLeftRight(pageInfo: IPageInfo) {

    if (!pageInfo) {
      return null;
    }

    const { dimensions } = pageInfo;
    const pageLeft = (this.scrollableWidth - dimensions.width) / 2;

    const pageRight = pageLeft + dimensions.width;

    return {
      pageLeft,
      pageRight
    }
  }


  getExtendedPage(pageInfo: IPageInfo): IExtendedPageInfo {

    const { pageLeft, pageRight } = this.getPageLeftRight(pageInfo);

    const nextPage = this.pdfPageInfos.find((page) => page.pageIdx === pageInfo.pageIdx + 1);
    const prevPage = this.pdfPageInfos.find((page) => page.pageIdx === pageInfo.pageIdx - 1);

    const nextLeftRight = this.getPageLeftRight(nextPage);
    const prevLeftRight = this.getPageLeftRight(prevPage);

    return {
      ...pageInfo,
      pageLeft,
      pageRight,
      nextPage: { ...nextPage, ...nextLeftRight },
      prevPage: { ...prevPage, ...prevLeftRight },
    }
  }


  private _scrollToSelectedPage() {

  }

  private _getPageIdxByScroll(scrollTop: number) {
    return this.pdfPageInfos.find((pageInfo) => (pageInfo.scrollTop + pageInfo.dimensions.height) > scrollTop);
  }


  ngOnDestroy() {

    this._destroy.next();
    this._destroy.complete();


  }
}
